import React, { useState, useEffect } from 'react';
import './CurrencyConversion.css';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:5001'
    : (typeof window !== 'undefined' ? window.location.origin : ''));


const CurrencyConversion = ({ walletBalances, onWalletUpdate, user, countryInfo }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // State for conversion form
  const [formData, setFormData] = useState({
    fromCurrency: 'USD',
    toCurrency: '',
    amount: '',
    conversionRate: '',
    convertedAmount: ''
  });
  
  // State for available rates
  const [availableRates, setAvailableRates] = useState({
    usdToLocal: 0,
    inrToLocal: 0,
    localToUsd: 0,
    localToInr: 0
  });
  
  // State for target currencies based on selection
  const [targetCurrencies, setTargetCurrencies] = useState([]);
  
  const localCurrency = countryInfo?.local_currency_code || 'USD';
  const countryName = countryInfo?.country_name || 'Unknown';

  // Fetch conversion rates on component mount
  useEffect(() => {
    fetchConversionRates();
    
    // Initialize toCurrency based on fromCurrency
    setFormData(prev => ({
      ...prev,
      toCurrency: localCurrency
    }));
  }, []);

  // Update target currencies when fromCurrency changes
  useEffect(() => {
    if (formData.fromCurrency) {
      updateTargetCurrencies(formData.fromCurrency);
    }
  }, [formData.fromCurrency, localCurrency]);

  // Update conversion rate when currencies change OR when rates finish loading
  useEffect(() => {
    if (formData.fromCurrency && formData.toCurrency) {
      updateConversionRate();
    }
  }, [formData.fromCurrency, formData.toCurrency, availableRates]);

  // Update converted amount when amount or rate changes
  useEffect(() => {
    if (formData.amount && formData.conversionRate) {
      const converted = parseFloat(formData.amount) * parseFloat(formData.conversionRate);
      setFormData(prev => ({
        ...prev,
        convertedAmount: converted.toFixed(2)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        convertedAmount: ''
      }));
    }
  }, [formData.amount, formData.conversionRate]);

  // Fetch conversion rates from backend
 const fetchConversionRates = async () => {
  try {
    setLoading(true);
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE}/api/admin/currency/rates`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        // Check if we have Super Admin rates
        if (data.super_admin_rates) {
          const rates = data.super_admin_rates;
          const derived = data.rates?.derived || [];
          
          // Find USD to Local rate
          const usdToLocal = derived.find(rate => 
            rate.from_currency === 'USD' && rate.to_currency === localCurrency
          )?.rate || rates.usd_to_local || 0;
          
          // Find INR to Local rate
          const inrToLocal = derived.find(rate => 
            rate.from_currency === 'INR' && rate.to_currency === localCurrency
          )?.rate || rates.inr_to_local || 0;
          
          // Calculate inverse rates
          const localToUsd = usdToLocal > 0 ? (1 / usdToLocal).toFixed(6) : 0;
          const localToInr = inrToLocal > 0 ? (1 / inrToLocal).toFixed(6) : 0;
          
          setAvailableRates({
            usdToLocal: parseFloat(usdToLocal) || 0,
            inrToLocal: parseFloat(inrToLocal) || 0,
            localToUsd: parseFloat(localToUsd) || 0,
            localToInr: parseFloat(localToInr) || 0
          });
          
          console.log('Using Super Admin rates:', {
            usdToLocal: parseFloat(usdToLocal) || 0,
            inrToLocal: parseFloat(inrToLocal) || 0,
            localToUsd: parseFloat(localToUsd) || 0,
            localToInr: parseFloat(localToInr) || 0
          });
          
          // Show info message if rates are locked
          if (data.super_admin_rates.note) {
            console.log('Rate info:', data.super_admin_rates.note);
          }
        } else {
          // Fallback to old logic if no Super Admin rates
          const rates = data.rates || {};
          const derived = rates.derived || [];
        }
      }
    } else {
      const errorData = await response.json();
      setError(errorData.error || 'Failed to fetch conversion rates');
    }
  } catch (error) {
    console.error('Error fetching conversion rates:', error);
    setError('Network error. Please try again.');
  } finally {
    setLoading(false);
  }
};

  // Update target currencies based on from currency selection
  const updateTargetCurrencies = (fromCurrency) => {
    const currencies = [];

    if (fromCurrency === 'USD') {
      currencies.push({ code: localCurrency, name: `${localCurrency} (Local)` });
    } else {
      currencies.push({ code: 'USD', name: 'US Dollar' });
    }

    setTargetCurrencies(currencies);

    if (currencies.length > 0) {
      setFormData(prev => ({
        ...prev,
        toCurrency: currencies[0].code
      }));
    }
  };

  // Update conversion rate based on selected currencies
  const updateConversionRate = () => {
    const { fromCurrency, toCurrency } = formData;
    let rate = 0;

    if (fromCurrency === 'USD' && toCurrency === localCurrency) {
      rate = availableRates.usdToLocal;   // USD -> local: amount * rate
    } else if (fromCurrency === localCurrency && toCurrency === 'USD') {
      rate = availableRates.localToUsd;   // local -> USD: amount / rate
    }

    setFormData(prev => ({
      ...prev,
      conversionRate: rate > 0 ? rate.toFixed(6) : '0.000000'
    }));
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear messages
    if (error || success) {
      setError('');
      setSuccess('');
    }
  };

  // Handle currency conversion
  const handleConvertCurrency = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    const { fromCurrency, toCurrency, amount } = formData;
    
    // Validation
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    // Check balance
    const amountNum = parseFloat(amount);
    let availableBalance = 0;
    
    switch(fromCurrency) {
      case 'USD':
        availableBalance = walletBalances.usd;
        break;
      case 'INR':
        availableBalance = walletBalances.inr;
        break;
      default:
        availableBalance = walletBalances.local;
    }
    
    if (amountNum > availableBalance) {
      setError(`Insufficient ${fromCurrency} balance. Available: ${availableBalance.toFixed(2)}`);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/currency/convert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          fromCurrency,
          toCurrency,
          amount: amountNum
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSuccess(data.message || 'Currency converted successfully!');
        
        // Reset form
        setFormData({
          fromCurrency: 'USD',
          toCurrency: localCurrency,
          amount: '',
          conversionRate: '',
          convertedAmount: ''
        });
        
        // Update wallet balances
        if (onWalletUpdate) {
          onWalletUpdate();
        }
        
        // Refresh rates
        fetchConversionRates();
      } else {
        setError(data.error || 'Conversion failed');
      }
    } catch (error) {
      console.error('Error converting currency:', error);
      setError('Conversion failed. Please try again.');
    }
  };

  // Get currency symbol
  const getCurrencySymbol = (currencyCode) => {
    const symbols = {
      'INR': '₹',
      'USD': '$',
      'LRD': 'L$',
      'XOF': 'CFA',
      'EUR': '€',
      'GBP': '£',
      'GHS': 'GH₵',
      'NGN': '₦'
    };
    
    // Use the symbol from countryInfo if it's the local currency
    if (currencyCode === localCurrency && countryInfo?.local_currency_symbol) {
      return countryInfo.local_currency_symbol;
    }
    
    return symbols[currencyCode] || currencyCode;
  };

  // Get available from currencies - Ensure unique keys
  const getAvailableFromCurrencies = () => {
    const currencies = [];

    // USD (main currency)
    currencies.push({
      code: 'USD',
      name: 'US Dollar',
      balance: walletBalances.usd,
      key: 'USD-from'
    });

    // Local currency (if different from USD)
    if (localCurrency && localCurrency !== 'USD') {
      const localBalance = localCurrency === 'INR' ? walletBalances.inr : walletBalances.local;
      currencies.push({
        code: localCurrency,
        name: `${localCurrency} (Local)`,
        balance: localBalance,
        key: `${localCurrency}-from`
      });
    }

    return currencies;
  };

  if (loading) {
    return (
      <div className="currency-conversion-loading">
        <div className="loading-spinner"></div>
        <p>Loading currency conversion...</p>
      </div>
    );
  }

  const availableFromCurrencies = getAvailableFromCurrencies();

  return (
    <div className="currency-conversion-simple">
      {/* Header */}
      <div className="conversion-header-simple">
        <h2><i className="fas fa-exchange-alt"></i> Currency Conversion</h2>
        <div className="header-info">
          <div className="country-info">
            <i className="fas fa-globe"></i>
            <span>Country: <strong>{countryName}</strong></span>
          </div>
          <div className="rate-info">
            <i className="fas fa-percentage"></i>
            <span>Current Rate: <strong>1 USD = {availableRates.usdToLocal || '0.00'} {localCurrency}</strong></span>
          </div>
        </div>
      </div>

      {error && <div className="error-message-simple">{error}</div>}
      {success && <div className="success-message-simple">{success}</div>}

      {/* Wallet Balances */}
      <div className="wallet-balances-simple">
        <h3><i className="fas fa-wallet"></i> Current Balances</h3>
        <div className="balance-cards">
          <div className="balance-card usd">
            <div className="balance-icon">
              <i className="fas fa-dollar-sign"></i>
            </div>
            <div className="balance-details">
              <h4>USD Balance</h4>
              <p className="balance-amount">
                {getCurrencySymbol('USD')}{walletBalances.usd.toFixed(2)}
              </p>
            </div>
          </div>
          
          <div className="balance-card inr">
            <div className="balance-icon">
              <i className="fas fa-rupee-sign"></i>
            </div>
            <div className="balance-details">
              <h4>INR Balance</h4>
              <p className="balance-amount">
                {getCurrencySymbol('INR')}{walletBalances.inr.toFixed(2)}
              </p>
            </div>
          </div>
          
          <div className="balance-card local">
            <div className="balance-icon">
              <i className="fas fa-landmark"></i>
            </div>
            <div className="balance-details">
              <h4>{localCurrency} Balance</h4>
              <p className="balance-amount">
                {getCurrencySymbol(localCurrency)}{walletBalances.local.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Conversion Form */}
      <div className="conversion-form-simple">
        <h3><i className="fas fa-sync-alt"></i> Convert Currency</h3>
        
        <form onSubmit={handleConvertCurrency}>
          <div className="form-grid">
            {/* From Currency */}
            <div className="form-group-simple">
              <label htmlFor="fromCurrency">
                <i className="fas fa-arrow-up"></i> From Currency
              </label>
              <select
                id="fromCurrency"
                name="fromCurrency"
                value={formData.fromCurrency}
                onChange={handleInputChange}
                className="currency-select-simple"
                style={{ color: '#000' }}
                required
              >
                <option value="" style={{ color: '#000' }}>Select source currency</option>
                {availableFromCurrencies.map((currency, index) => (
                  <option 
                    key={`${currency.code}-${index}-from`}
                    value={currency.code}
                    style={{ color: '#000' }}
                  >
                    {currency.code} - {currency.name} (Available: {getCurrencySymbol(currency.code)}{currency.balance.toFixed(2)})
                  </option>
                ))}
              </select>
            </div>

            {/* To Currency */}
            <div className="form-group-simple">
              <label htmlFor="toCurrency">
                <i className="fas fa-arrow-down"></i> To Currency
              </label>
              <select
                id="toCurrency"
                name="toCurrency"
                value={formData.toCurrency}
                onChange={handleInputChange}
                className="currency-select-simple"
                style={{ color: '#000' }}
                required
                disabled={targetCurrencies.length === 0}
              >
                <option value="" style={{ color: '#000' }}>Select target currency</option>
                {targetCurrencies.map((currency, index) => (
                  <option 
                    key={`${currency.code}-${index}-to`}
                    value={currency.code}
                    style={{ color: '#000' }}
                  >
                    {currency.code} - {currency.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Conversion Rate Display */}
          {formData.fromCurrency && formData.toCurrency && formData.conversionRate && (
            <div className="rate-display-simple">
              <div className="rate-card">
                <i className="fas fa-percentage"></i>
                <div className="rate-text">
                  <span className="rate-label">Conversion Rate:</span>
                  <span className="rate-value">
                    1 {formData.fromCurrency} = {formData.conversionRate} {formData.toCurrency}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Amount Inputs */}
          <div className="amount-grid">
            <div className="form-group-simple">
              <label htmlFor="amount">
                <i className="fas fa-money-bill-wave"></i> Amount to Convert ({formData.fromCurrency})
              </label>
              <div className="amount-input-simple">
                <span className="currency-prefix-simple">
                  {getCurrencySymbol(formData.fromCurrency)}
                </span>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="Enter amount"
                  min="0.01"
                  step="0.01"
                  required
                />
              </div>
            </div>

            <div className="form-group-simple">
              <label htmlFor="convertedAmount">
                <i className="fas fa-calculator"></i> Converted Amount ({formData.toCurrency})
              </label>
              <div className="amount-input-simple">
                <span className="currency-prefix-simple">
                  {formData.toCurrency ? getCurrencySymbol(formData.toCurrency) : '$'}
                </span>
                <input
                  type="text"
                  id="convertedAmount"
                  name="convertedAmount"
                  value={formData.convertedAmount}
                  readOnly
                  placeholder="Will be calculated"
                  className="readonly-input"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="form-actions-simple">
            <button 
              type="submit" 
              className="convert-btn-simple"
              disabled={!formData.amount || !formData.conversionRate}
            >
              <i className="fas fa-exchange-alt"></i>
              Convert Currency
            </button>
            <button 
              type="button" 
              className="refresh-btn-simple"
              onClick={fetchConversionRates}
            >
              <i className="fas fa-sync-alt"></i>
              Refresh Rates
            </button>
          </div>
        </form>
      </div>

      {/* Quick Info */}
      <div className="quick-info-simple">
        <h4><i className="fas fa-info-circle"></i> Quick Info</h4>
        <ul>
          <li>1 USD = {availableRates.usdToLocal || 'N/A'} {localCurrency}</li>
          <li>1 {localCurrency} = {availableRates.localToUsd || 'N/A'} USD</li>
        </ul>
      </div>
    </div>
  );
};

export default CurrencyConversion;